/* eslint-disable no-bitwise */
import SparkMD5 from 'spark-md5';

// cyrb53 (c) 2018 bryc (github.com/bryc). License: Public domain. Attribution appreciated.
// A fast and simple 64-bit (or 53-bit) string hash function with decent collision resistance.
// Largely inspired by MurmurHash2/3, but with a focus on speed/simplicity.
// See https://stackoverflow.com/questions/7616461/generate-a-hash-from-string-in-javascript/52171480#52171480
// https://github.com/bryc/code/blob/master/jshash/experimental/cyrb53.js
// https://gist.github.com/jlevy/c246006675becc446360a798e2b2d781
const cyrb53 = (str: string, seed = 0) => {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let i = 0, ch; i < str.length; i++) {
    ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);

  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
};
const cyrb64 = (str: string, seed = 0) => {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let i = 0, ch; i < str.length; i++) {
    ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return [h2 >>> 0, h1 >>> 0];
};
// An improved, *insecure* 64-bit hash that's short, fast, and has no dependencies.
// Output is always 14 characters.
const cyrb64Hash = (str: string, seed = 0) => {
  const [h2, h1] = cyrb64(str, seed);
  return h2.toString(36).padStart(7, '0') + h1.toString(36).padStart(7, '0');
};

/**
 * Creates a hash of items using the provided hashing algorithm
 * @param items items to be hashed
 * @param hashAlgorithm hashing algorithm to use
 * @returns hash of items
 */
export function generateHash(items: any[], hashAlgorithm: (val: string) => string): string {
  const arr = [];
  for (let item of items) {
    if (item == null) {
      continue;
    }
    if (typeof item !== 'string') {
      item = JSON.stringify(item);
    }
    arr.push(item);
  }
  const hash = hashAlgorithm(arr.join(''));
  return hash;
}

/**
 * Create a hash of items prefixed with name and separated by a pipe.
 * @param name name of hash
 * @param itemsToHash items to be hashed
 * @param hashAlgorithm hashing algorithm to use
 * @returns hash prefixed with name and separated by pipe (|)
 */
export function generateNamedHash(
  name: string,
  itemsToHash: any[],
  hashAlgorithm: (val: string) => string,
): string {
  return `${name}|${generateHash(itemsToHash, hashAlgorithm)}`;
}

/**
 * Creates an MD5 hash of items
 * @param items items to be hashed
 * @returns MD5 hash of items
 */
export function generateMD5Hash(...items: any[]) {
  return generateHash(items, HashAlgorithm.md5);
}

/**
 * Create an MD5 hash of items prefixed with name and separated by a pipe.
 * @param name name of hash
 * @param itemsToHash items to be hashed
 * @returns MD5 hash prefixed with name and separated by pipe (|)
 */
export function generateNamedMD5Hash(name: string, itemsToHash: any[]) {
  return generateNamedHash(name, itemsToHash, HashAlgorithm.md5);
}

function createHashAlgorithmFactory() {
  return {
    none: (str: string) => str,
    cyrb64: cyrb64Hash,
    cyrb53: (str: string) => cyrb53(str).toString(16),
    md5: (str: string) => SparkMD5.hash(str),
  };
}

export const HashAlgorithm = createHashAlgorithmFactory();
